import React, { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { Modal, Switch } from 'antd';
import {
  arrowBackOutline,
  documentTextOutline,
  shieldCheckmarkOutline,
  analyticsOutline,
  bugOutline,
  mailOutline,
  informationCircleOutline,
  checkmarkCircleOutline,
  closeOutline,
  chevronForwardOutline,
} from 'ionicons/icons';
import './SettingsPage.css';

const EULA_CONTENT = `END USER LICENSE AGREEMENT (EULA)

Last updated: March 2025

This End User License Agreement ("Agreement") is a legal agreement between you ("User," "you," or "your") and SvgTrace ("Company," "we," "us," or "our") regarding your use of the SvgTrace application ("App").

BY DOWNLOADING, INSTALLING, OR USING THE APP, YOU AGREE TO BE BOUND BY THIS AGREEMENT. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE APP.

1. LICENSE GRANT
Subject to your compliance with this Agreement, we grant you a limited, non-exclusive, non-transferable, revocable license to download, install, and use the App solely for your personal or commercial design work.

2. PURCHASE TERMS
• Subscription or one-time purchase options available (pricing varies by region)
• Features: Image-to-SVG conversion, AI generation, retrace, modify, resize, bulk convert, and all premium features
• Payment processed through your App Store, Google Play, or Stripe account
• Refunds subject to applicable store or payment provider refund policies
• Future major updates may require an additional purchase or subscription renewal

3. RESTRICTIONS AND PROHIBITED USES
You agree NOT to:
• Use the App for any unlawful purpose or in violation of any applicable laws
• Reverse engineer, decompile, disassemble, or attempt to derive source code
• Modify, adapt, alter, translate, or create derivative works
• Distribute, sell, rent, lease, or sublicense the App
• Remove, alter, or obscure any proprietary notices
• Use the App to violate the privacy or rights of others
• Upload malicious code, viruses, or harmful content
• Attempt to gain unauthorized access to our systems
• Use automated tools to access or use the App
• Share your account credentials with others

4. INTELLECTUAL PROPERTY
The App and all content, features, functionality, and technology are owned by SvgTrace and protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws. You acknowledge that you do not acquire any ownership rights by using the App.

5. USER CONTENT
• You retain ownership of images you upload for conversion and editing
• You grant us a limited license to process and analyze your uploaded content
• We do not store your images permanently unless you save them to your cloud files
• You are responsible for ensuring you have rights to upload any content
• You agree not to upload inappropriate, illegal, or copyrighted content without authorization

6. THIRD-PARTY SERVICES
The App integrates with third-party services including but not limited to:
• Firebase (Google) for backend services and authentication
• Stripe for web payment processing
• Apple App Store / Google Play for mobile payment processing
• RevenueCat for subscription management (iOS)
Your use of these services is subject to their respective terms of service.

7. PRIVACY AND DATA COLLECTION
Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated by reference into this Agreement. By using the App, you consent to our Privacy Policy.

8. DISCLAIMERS AND WARRANTIES
THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

• SVG conversion results are algorithmic approximations and may not be pixel-perfect
• AI-generated content may vary in quality and accuracy
• We do not guarantee the App will be error-free or uninterrupted
• You use the App at your own risk

9. LIMITATION OF LIABILITY
TO THE MAXIMUM EXTENT PERMITTED BY LAW, SVGTRACE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING FROM YOUR USE OF THE APP, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE APP IN THE 12 MONTHS PRECEDING THE CLAIM.

10. INDEMNIFICATION
You agree to indemnify, defend, and hold harmless SvgTrace from any claims, damages, losses, or expenses arising from your use of the App or violation of this Agreement.

11. AGE REQUIREMENTS
The App is not intended for children under 13 years of age. If you are under 18, you represent that your parent or guardian has reviewed and agreed to this Agreement.

12. UPDATES AND MODIFICATIONS
• We may update the App to fix bugs, add features, or improve performance
• We may modify this Agreement at any time by posting the updated terms
• Continued use after changes constitutes acceptance of new terms
• Material changes will be notified through the App or email

13. TERMINATION
• This Agreement is effective until terminated
• You may terminate by deleting the App and cancelling subscriptions
• We may terminate or suspend access for violations of this Agreement
• Upon termination, all rights granted to you cease immediately

14. DISPUTE RESOLUTION
• Any disputes will be resolved through binding arbitration
• Arbitration will be conducted under the rules of the American Arbitration Association
• You waive the right to participate in class action lawsuits

15. SEVERABILITY
If any provision of this Agreement is found to be unenforceable, the remainder will remain in full force and effect.

16. GOVERNING LAW
This Agreement is governed by the laws of the State of Delaware, United States, without regard to conflict of law principles.

17. CONTACT INFORMATION
For questions about this Agreement, contact us at: support@svgtrace.com

BY USING THE APP, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THIS AGREEMENT.`;

const PRIVACY_POLICY = `PRIVACY POLICY

Last updated: March 2025

SvgTrace ("we," "our," or "us") respects your privacy and is committed to protecting your personal information.

1. INFORMATION WE COLLECT
• Account information (email, name) via Firebase Authentication
• App usage data and preferences
• Device information (device type, OS version)
• Images you choose to convert or edit
• Crash reports and performance data

2. HOW WE USE INFORMATION
• Provide and improve our services
• Process image conversion and AI generation requests
• Send important updates about your account
• Analyze app usage to improve features
• Prevent fraud and ensure security

3. DATA SHARING
We do not sell your personal information. We may share data with:
• Service providers (Firebase, Stripe, RevenueCat) who help operate our app
• Analytics providers to improve our services
• Law enforcement if required by law

4. DATA SECURITY
We use industry-standard security measures to protect your information:
• Encryption in transit and at rest
• Secure authentication systems (Firebase Auth)
• Regular security audits
• Limited access to personal data

5. YOUR RIGHTS
You have the right to:
• Access your personal information
• Correct inaccurate information
• Delete your account and data
• Opt out of marketing communications
• Request a copy of your data

6. DATA RETENTION
• Account data: Until you delete your account
• Uploaded images: Retained while in your cloud storage; deleted upon request
• Usage data: Retained for up to 2 years
• Analytics data: Anonymized after 6 months

7. CHILDREN'S PRIVACY
Our App is not intended for children under 13. We do not knowingly collect information from children.

8. INTERNATIONAL USERS
Your information may be processed in countries other than your own. We ensure adequate protection regardless of location.

9. COOKIES AND TRACKING
We use cookies and similar technologies (on web) to improve your experience and analyze usage patterns.

10. CHANGES TO POLICY
We may update this policy and will notify you of significant changes through the App.

11. CONTACT US
For privacy questions or to exercise your rights, contact us at:
support@svgtrace.com

By using SvgTrace, you consent to this Privacy Policy.`;

const PrivacySettingsPage = ({ onBack }) => {
  const [eulaOpen, setEulaOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [preferences, setPreferences] = useState({
    dataCollection: true,
    analytics: true,
    crashReports: true,
    marketing: false,
  });

  const handleToggle = (key) => (checked) => {
    setPreferences((prev) => ({ ...prev, [key]: checked }));
  };

  const legalDocs = [
    {
      icon: documentTextOutline,
      title: 'End User License Agreement (EULA)',
      subtitle: 'Review the terms of service',
      action: () => setEulaOpen(true),
    },
    {
      icon: shieldCheckmarkOutline,
      title: 'Privacy Policy',
      subtitle: 'How we handle your data',
      action: () => setPrivacyOpen(true),
    },
  ];

  const privacyPreferences = [
    {
      key: 'dataCollection',
      icon: informationCircleOutline,
      title: 'Data Collection',
      description: 'Allow SvgTrace to collect usage data to improve the app',
      required: true,
    },
    {
      key: 'analytics',
      icon: analyticsOutline,
      title: 'Analytics & Performance',
      description: 'Help us understand how the app is used and improve performance',
      required: false,
    },
    {
      key: 'crashReports',
      icon: bugOutline,
      title: 'Crash Reports',
      description: 'Automatically send crash reports to help us fix issues',
      required: false,
    },
    {
      key: 'marketing',
      icon: mailOutline,
      title: 'Marketing Communications',
      description: 'Receive occasional updates about new features and improvements',
      required: false,
    },
  ];

  const userRights = [
    'Request a copy of your data',
    'Correct inaccurate information',
    'Delete your account and all data',
    'Opt out of marketing communications',
  ];

  return (
    <div className="settings-page">
      <div className="sp-header">
        <button className="sp-back-btn" onClick={onBack}>
          <IonIcon icon={arrowBackOutline} />
        </button>
        <h2 className="sp-header-title">Privacy & Security</h2>
      </div>

      <div className="sp-scroll">
        {/* Legal Documents */}
        <div className="sp-section">
          <p className="sp-section-title">Legal Documents</p>
          {legalDocs.map((item, i) => (
            <div key={i} className="sp-item" onClick={item.action}>
              <div
                className="sp-item-icon"
                style={{ background: '#f1f5f9', color: '#64748b' }}
              >
                <IonIcon icon={item.icon} />
              </div>
              <div className="sp-item-body">
                <p className="sp-item-title">{item.title}</p>
                <p className="sp-item-sub">{item.subtitle}</p>
              </div>
              <IonIcon icon={chevronForwardOutline} className="sp-item-chevron" />
            </div>
          ))}
        </div>

        {/* Privacy Preferences */}
        <div className="sp-section">
          <p className="sp-section-title">Privacy Preferences</p>
          <p style={{ fontSize: 13, color: '#94a3b8', padding: '0 18px 8px', margin: 0, lineHeight: 1.4 }}>
            Control how your data is used to improve SvgTrace. You can change these settings anytime.
          </p>
          {privacyPreferences.map((pref) => (
            <div key={pref.key} className="sp-toggle-row">
              <div
                className="sp-item-icon"
                style={{ background: '#f1f5f9', color: '#64748b' }}
              >
                <IonIcon icon={pref.icon} />
              </div>
              <div className="sp-toggle-body">
                <div className="sp-toggle-heading">
                  <span className="sp-toggle-label">{pref.title}</span>
                  {pref.required && (
                    <span className="sp-toggle-required">Required</span>
                  )}
                </div>
                <p className="sp-toggle-desc">{pref.description}</p>
              </div>
              <Switch
                checked={preferences[pref.key]}
                onChange={handleToggle(pref.key)}
                disabled={pref.required}
                size="small"
              />
            </div>
          ))}
        </div>

        {/* Your Data Rights */}
        <div className="sp-section">
          <p className="sp-section-title">Your Data Rights</p>
          <p style={{ fontSize: 13, color: '#94a3b8', padding: '0 18px 10px', margin: 0, lineHeight: 1.4 }}>
            You have control over your personal data:
          </p>
          <div className="sp-rights-list">
            {userRights.map((right, i) => (
              <div key={i} className="sp-rights-item">
                <IonIcon icon={checkmarkCircleOutline} className="sp-rights-icon" />
                <span>{right}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* EULA Dialog */}
      <Modal
        open={eulaOpen}
        onCancel={() => setEulaOpen(false)}
        footer={null}
        width={600}
        className="sp-dialog"
        centered
        destroyOnClose
      >
        <div className="sp-dialog-header">
          <div className="sp-dialog-header-left">
            <IonIcon icon={documentTextOutline} className="sp-dialog-header-icon" />
            <h3 className="sp-dialog-title">End User License Agreement</h3>
          </div>
          <button className="sp-dialog-close" onClick={() => setEulaOpen(false)}>
            <IonIcon icon={closeOutline} />
          </button>
        </div>
        <div className="sp-dialog-body">{EULA_CONTENT}</div>
        <div className="sp-dialog-footer">
          <button className="sp-btn-primary" onClick={() => setEulaOpen(false)}>
            I Understand
          </button>
        </div>
      </Modal>

      {/* Privacy Policy Dialog */}
      <Modal
        open={privacyOpen}
        onCancel={() => setPrivacyOpen(false)}
        footer={null}
        width={600}
        className="sp-dialog"
        centered
        destroyOnClose
      >
        <div className="sp-dialog-header">
          <div className="sp-dialog-header-left">
            <IonIcon icon={shieldCheckmarkOutline} className="sp-dialog-header-icon" />
            <h3 className="sp-dialog-title">Privacy Policy</h3>
          </div>
          <button className="sp-dialog-close" onClick={() => setPrivacyOpen(false)}>
            <IonIcon icon={closeOutline} />
          </button>
        </div>
        <div className="sp-dialog-body">{PRIVACY_POLICY}</div>
        <div className="sp-dialog-footer">
          <button className="sp-btn-primary" onClick={() => setPrivacyOpen(false)}>
            I Understand
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PrivacySettingsPage;
